import React, { useState, useEffect } from "react";

import { EvacuationUserAddition, EvacueeRequest } from "../../../types/emergency";

import {
  EvacuationGroup,
  EvacuationGroupBuilding,
  postEvacuee,
  postEvacueeAddition,
} from "../../../services/managerEmergencyServices";

import useSimplePopup from "../../../hooks/useSimplePopup"; // useSimplePopup의 경로에 맞게 수정하세요.

import CommonPopup from "..";
import CommonButton from "../../Button";
import CommonInput from "../../Input/CommonInput";
import CommonLabel from "../../Label";
import {
  BuildingFloorItem,
  getBuildingFloorList,
} from "../../../services/buildingServices";
import { BuildingId } from "../../../types/building";
import { HttpStatusCode } from "axios";

export default function RegistrationForm({
  isOpen,
  onClose,
  evacuationGroupId,
  evacuationId,
  onRegistrationSuccess, // 부모로부터 전달받은 새로고침 함수
  groupBuilding,
  setFloorLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  evacuationGroupId: string;
  evacuationId: string;
  onRegistrationSuccess: () => void; // 새로고침 함수의 타입 정의
  groupBuilding?: EvacuationGroup;
  setFloorLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [name, setName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [building, setBuilding] = useState<EvacuationGroupBuilding | undefined>(
    undefined
  );
  const [evacuationCompanyId, setCompany] = useState("");
  const [evacuationDepartmentName, setDepartment] = useState("");
  const [evacuationFloorId, setEvacuationFloorId] = useState<string>("");
  const [floorList, setFloorList] = useState<BuildingFloorItem[]>([]);
  const status = "CREATED_NO_RESPONSE"; // 기본값으로 설정

  // useSimplePopup 훅 사용
  const [simplePopupComponent, { open, openError }] = useSimplePopup({});

  // 폼 초기화: 팝업이 열릴 때 필드를 초기화
  useEffect(() => {
    if (isOpen) {
      setName("");
      setMobileNumber("");
      setBuilding(undefined);
      setCompany("");
      setDepartment("");
      setFloorList([]);
      setEvacuationFloorId("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (building && building.buildingId) {
      fetchBuildingFloorList(building as BuildingId);
    }
  }, [building]);

  const fetchBuildingFloorList = async (building: BuildingId) => {
    try {
      setFloorLoading(true);
      const response = await getBuildingFloorList(building);
      if (response.status === HttpStatusCode.Ok) {
        setFloorList(response.data);
      } else {
        throw new Error("층 정보 조회중 오류가 발생했습니다.");
      }
    } catch (err: any) {
      openError(err, "층 정보 조회중 오류가 발생했습니다.");
    } finally {
      setFloorLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 이름 및 연락처 입력값 검증
    const nameRegex = /^[a-zA-Z가-힣]+$/; // 영문, 한글만 허용
    const mobileRegex = /^[0-9]+$/; // 숫자만 허용

    if (!name.trim() || !nameRegex.test(name)) {
      open("빈 값이나 특수문자를 입력할 수 없습니다.");
      throw new Error("이름을 확인해주세요.");
    }
    if (!mobileNumber.trim() || !mobileRegex.test(mobileNumber)) {
      open("빈 값이나 특수문자를 입력할 수 없습니다.");
      throw new Error("연락처를 확인해주세요.");
    }
    if (!building?.buildingId?.trim()) {
      open("건물을 선택해주세요.");
      throw new Error("건물을 선택해주세요.");
    }
    if (!evacuationCompanyId.trim()) {
      open("회사를 선택해주세요.");
      throw new Error("회사를 선택해주세요.");
    }
    if (!evacuationFloorId.trim()) {
      open("근무 층을 선택해주세요.");
      throw new Error("근무 층을 선택해주세요.");
    }
    if (!evacuationDepartmentName.trim()) {
      open("부서를 입력해주세요.");
      throw new Error("부서를 입력해주세요.");
    }

    // const evacueeData: EvacueeRequest = {
    //   name,
    //   mobileNumber,
    //   evacuationBuildingId: building.buildingId,
    //   evacuationCompanyId,
    //   evacuationDepartmentName,
    //   evacuationFloorId,
    //   evacuationGroupId,
    //   evacuationId,
    //   status,
    // };

    const evacueeData: Partial<EvacuationUserAddition>[] = [
      {
        name,
        mobileNumber,
        evacuationBuildingId: building.buildingId,
        evacuationCompanyId,
        evacuationDepartmentName,
        evacuationFloorId,
        evacuationGroupId,
        evacuationId,
        role: "ROLE_USER",
      },
    ];
    try {
      // await postEvacuee(evacueeData);
      await postEvacueeAddition(evacuationId, evacueeData);
      open("등록이 성공적으로 완료되었습니다."); // 성공 메시지 표시
      onRegistrationSuccess(); // 부모 컴포넌트의 새로고침 함수 호출
      onClose(); // 팝업 닫기
    } catch (error) {
      open("등록 중 오류가 발생했습니다."); // 실패 메시지 표시
      console.error(error);
    }
  };

  return (
    <>
      <CommonPopup
        isOpen={isOpen}
        onClose={onClose}
        title="인원 등록"
        unUseHorizonRule
        padding={0}
      >
        <div className="px-5 pb-5 flex flex-col gap-4">
          {/* Name Field */}
          <div>
            <CommonLabel name="name" required label="이름" />
            <CommonInput
              type="text"
              id="name"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
              placeholder="이름을 입력해주세요."
              value={name}
              onChange={(value) => setName(value)}
            />
          </div>

          {/* Mobile Number Field */}
          <div>
            <CommonLabel name="mobileNumber" required label="연락처" />
            <CommonInput
              type="text"
              id="mobileNumber"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
              placeholder="연락처를 입력해주세요."
              value={mobileNumber}
              onChange={(value) => setMobileNumber(value)}
            />
          </div>

          <div>
            <CommonLabel name="evacuationBuildingId" label="건물" required />
            <select
              id="evacuationBuildingId"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm h-[50px]"
              value={building?.buildingId ?? ""}
              onChange={(e) => {
                const selectedBuilding = groupBuilding?.buildingList.find(
                  (building) => building.buildingId === e.target.value
                );
                if (!selectedBuilding) {
                  open("잘못된 건물입니다.");
                  return;
                } else {
                  setBuilding(selectedBuilding);
                }
              }}
            >
              <option value="">건물을 선택해주세요.</option>
              {/* 실제 건물 옵션 추가 */}
              {groupBuilding?.buildingList.map((building, index) => (
                <option key={index} value={building.buildingId as string}>
                  {building.buildingName}
                </option>
              ))}
              {/* 다른 건물 옵션 */}
            </select>
          </div>

          {/* Company Field */}
          <div>
            <CommonLabel name="evacuationCompanyId" label="회사" required />
            <select
              id="evacuationCompanyId"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm h-[50px]"
              value={evacuationCompanyId}
              onChange={(e) => setCompany(e.target.value)}
            >
              <option value="">회사를 선택해주세요.</option>
              {/* 실제 회사 옵션 추가 */}
              {building?.companyShelterMapDtoList.map((company, index) => (
                <option key={index} value={company.companyId as string}>
                  {company.companyName}
                </option>
              ))}
              {/* 다른 회사 옵션 */}
            </select>
          </div>

          {/* Floor Field */}
          <div>
            <CommonLabel name="evacuationFloorId" label="근무 층" required />
            <select
              id="evacuationFloorId"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm h-[50px]"
              value={evacuationFloorId}
              onChange={(e) => setEvacuationFloorId(e.target.value)}
            >
              <option value="">근무 층을 선택해주세요.</option>
              {floorList.map((floor, index) => (
                <option key={index} value={floor.buildingFloorId as string}>
                  {floor.floorName}
                </option>
              ))}
            </select>
          </div>

          {/* Department Field */}
          <div>
            <CommonLabel
              name="evacuationDepartmentName"
              label="부서"
              required
            />
            <input
              type="text"
              id="evacuationDepartmentName"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
              placeholder="부서를 입력해주세요."
              value={evacuationDepartmentName}
              onChange={(e) => setDepartment(e.target.value)}
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-between h-bottom-button">
          <CommonButton
            variant="secondary"
            onClick={onClose}
            className="min-w-[100px] rounded-none"
          >
            취소
          </CommonButton>
          <CommonButton
            variant="primary"
            onClick={handleSubmit}
            className="rounded-none w-full"
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>
      {simplePopupComponent}
    </>
  );
}
